import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Wallet,
  IndianRupee,
  Clock,
  CheckCircle2,
  FileText,
  RefreshCw,
  Download,
  AlertTriangle,
  Building2,
  BadgeCheck,
} from "lucide-react";
import { DatePicker, Modal, Input, InputNumber, Select, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { dsaApi } from "../services/api";

const { RangePicker } = DatePicker;

// DSA me commission tabhi banta hai jab lender file disburse karta hai
const STATUS_CONFIG = {
  PENDING: { label: "Pending", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  PARTIAL: { label: "Partial", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  RECEIVED: { label: "Received", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
};

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Stat card ke liye — value na mile to "—" dikhao, zero mat maan lo
const formatINROrDash = (val) => (val === null || val === undefined ? "—" : formatINR(val));

// String/number dono handle karo, warna null
const num = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const n = typeof val === "string" ? Number(val.replace(/[^0-9.-]/g, "")) : Number(val);
  return Number.isFinite(n) ? n : null;
};

// Backend key naming alag ho sakti hai — pehli maujood key utha lo
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return undefined;
};

// Response shape ko lekar tolerant raho (page / wrapper / plain array)
const toArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.content)) return res.content;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.content)) return res.data.content;
  if (Array.isArray(res?.commissions)) return res.commissions;
  if (Array.isArray(res?.lenders)) return res.lenders;
  return [];
};

const fmtDate = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD/MM/YYYY") : "—");

// Row ko ek fixed shape me laao — table/CSV dono isi ko padhte hain
const normalizeCommission = (raw = {}) => {
  const disbursedAmount = num(
    pick(raw, ["disbursedAmount", "disbursalAmount", "disbursementAmount", "sanctionedAmount", "loanAmount"])
  );
  const commissionPercent = num(
    pick(raw, ["commissionPercentage", "commissionPercent", "commissionRate", "payoutPercentage", "payoutPercent"])
  );
  let expectedCommission = num(
    pick(raw, ["expectedCommission", "expectedCommissionAmount", "expectedAmount", "commissionAmount", "payoutAmount"])
  );
  // Backend ne expected na bheja ho par dono base values ho to calculate kar lo
  if (expectedCommission === null && disbursedAmount !== null && commissionPercent !== null) {
    expectedCommission = (disbursedAmount * commissionPercent) / 100;
  }

  const status = String(pick(raw, ["status", "commissionStatus", "payoutStatus"]) || "").toUpperCase();

  return {
    id: pick(raw, ["id", "commissionId", "payoutId"]),
    applicationId: pick(raw, [
      "applicationId",
      "loanApplicationId",
      "applicationNumber",
      "appId",
      "fileNumber",
      "loanId",
    ]),
    applicantName: pick(raw, ["applicantName", "customerName", "borrowerName", "applicant", "leadName"]),
    lenderName:
      pick(raw, ["lenderName", "bankName", "partnerName"]) ||
      pick(raw.lender || {}, ["name", "lenderName", "bankName"]),
    lenderId: pick(raw, ["lenderId"]) || pick(raw.lender || {}, ["id", "lenderId"]),
    disbursedAmount,
    commissionPercent,
    expectedCommission,
    receivedAmount: num(pick(raw, ["receivedAmount", "amountReceived", "receivedCommission", "paidAmount"])),
    status,
    disbursalDate: pick(raw, ["disbursalDate", "disbursedDate", "disbursementDate", "disbursedOn", "disbursedAt"]),
  };
};

export default function DsaCommissions() {
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "#2e856e";
  const { hasAny } = usePermissions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [lenders, setLenders] = useState([]);
  const [lendersError, setLendersError] = useState(null);

  // Filters
  const [lenderFilter, setLenderFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState(null);

  // Mark Received modal
  const [receiveModal, setReceiveModal] = useState({
    open: false,
    row: null,
    receivedAmount: null,
    reference: "",
    receivedDate: dayjs(),
    remarks: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  // ── Lender dropdown ── (fail ho to dropdown khali rahega, fake list nahi)
  const fetchLenders = useCallback(async () => {
    setLendersError(null);
    try {
      const res = await dsaApi.getLenders({ active: true });
      const list = toArray(res).map((l) => ({
        value: String(pick(l, ["id", "lenderId"]) ?? ""),
        label: String(pick(l, ["name", "lenderName", "bankName"]) ?? "Unnamed lender"),
      }));
      setLenders(list.filter((l) => l.value));
    } catch (err) {
      console.error("DSA lenders load failed:", err);
      setLendersError(err?.message || "Failed to load lenders");
      setLenders([]);
    }
  }, []);

  // ── Commission list ──
  const fetchCommissions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (lenderFilter !== "ALL") params.lenderId = lenderFilter;
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const res = await dsaApi.getCommissions(params);
      setRows(toArray(res).map(normalizeCommission));
    } catch (err) {
      console.error("DSA commissions load failed:", err);
      // Sample payout kabhi nahi dikhayenge — error + Retry
      setLoadError(err?.message || "Failed to load DSA commissions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [lenderFilter, statusFilter, dateRange]);

  useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  useEffect(() => {
    fetchCommissions();
  }, [fetchCommissions]);

  // Safety net — backend filter ignore kare to bhi screen par sahi rows rahein
  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        const matchesLender = lenderFilter === "ALL" || String(r.lenderId ?? "") === String(lenderFilter);
        const matchesStatus = statusFilter === "ALL" || r.status === statusFilter;
        return matchesLender && matchesStatus;
      }),
    [rows, lenderFilter, statusFilter]
  );

  // ── Stat cards sirf loaded data se — kuch na mile to null (UI me "—") ──
  const stats = useMemo(() => {
    if (loadError || !filteredRows.length) {
      return { expected: null, received: null, pending: null, disbursedFiles: null };
    }
    let expected = null;
    let received = null;
    let disbursedFiles = 0;

    filteredRows.forEach((r) => {
      if (r.expectedCommission !== null) expected = (expected ?? 0) + r.expectedCommission;
      if (r.receivedAmount !== null) received = (received ?? 0) + r.receivedAmount;
      if (r.disbursalDate || (r.disbursedAmount !== null && r.disbursedAmount > 0)) disbursedFiles += 1;
    });

    const pending = expected === null ? null : Math.max(expected - (received ?? 0), 0);
    return { expected, received, pending, disbursedFiles };
  }, [filteredRows, loadError]);

  // ── Mark Received ──
  const openReceiveModal = (row) => {
    const outstanding =
      row.expectedCommission !== null ? Math.max(row.expectedCommission - (row.receivedAmount ?? 0), 0) : null;
    setReceiveModal({
      open: true,
      row,
      receivedAmount: outstanding,
      reference: "",
      receivedDate: dayjs(),
      remarks: "",
    });
  };

  const closeReceiveModal = () =>
    setReceiveModal({ open: false, row: null, receivedAmount: null, reference: "", receivedDate: dayjs(), remarks: "" });

  const handleConfirmReceived = async () => {
    const { row, receivedAmount, reference, receivedDate, remarks } = receiveModal;
    if (!row?.id) {
      message.warning("Commission ID missing, cannot update this record");
      return;
    }
    if (!(Number(receivedAmount) > 0)) {
      message.warning("Please enter a valid received amount");
      return;
    }
    if (!receivedDate) {
      message.warning("Please select the received date");
      return;
    }

    setActionLoading(true);
    try {
      await dsaApi.markCommissionReceived(row.id, {
        receivedAmount: Number(receivedAmount),
        payoutReference: reference.trim(),
        utrNumber: reference.trim(),
        receivedDate: receivedDate.format("YYYY-MM-DD"),
        remarks: remarks.trim(),
      });
      // Success message sirf API pass hone par
      message.success(`Commission payout recorded for ${row.applicationId || "this file"}`);
      closeReceiveModal();
      fetchCommissions();
    } catch (err) {
      console.error("Mark commission received failed:", err);
      message.error(err?.message || "Failed to record commission payout");
    } finally {
      setActionLoading(false);
    }
  };

  // 📥 Client-side CSV — sirf jo rows abhi table me dikh rahi hain wahi jaayengi
  const handleExportCSV = () => {
    if (!filteredRows.length) {
      message.warning("No commission rows in the current view to export");
      return;
    }
    const headers = [
      "Application ID",
      "Applicant",
      "Lender",
      "Disbursed Amount",
      "Commission %",
      "Expected Commission",
      "Received Amount",
      "Status",
      "Disbursal Date",
    ];
    const rowsCsv = filteredRows.map((r) => [
      `"${r.applicationId || ""}"`,
      `"${(r.applicantName || "").replace(/"/g, '""')}"`,
      `"${(r.lenderName || "").replace(/"/g, '""')}"`,
      r.disbursedAmount ?? "",
      r.commissionPercent ?? "",
      r.expectedCommission ?? "",
      r.receivedAmount ?? "",
      `"${r.status || ""}"`,
      `"${r.disbursalDate ? dayjs(r.disbursalDate).format("DD/MM/YYYY") : ""}"`,
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," + [headers.join(","), ...rowsCsv.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `dsa_commissions_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("Current view exported to CSV");
  };

  // Edit-level code abhi backend me na ho to page-level view code se hi allow karo
  // (ADMIN ko PermissionContext khud bypass deta hai)
  const canMarkReceived = hasAny(["DSA_COMMISSION_EDIT", "DSA_COMMISSION_MANAGE", "DSA_COMMISSION_VIEW"]);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">DSA Commissions</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Payout tracking for files disbursed by partner banks & NBFCs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchCommissions}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-[#2e856e]/30 bg-[#2e856e]/5 text-[#2e856e] shadow-sm transition-all hover:bg-[#2e856e]/10 active:scale-95"
            title="Download the rows currently shown as CSV"
          >
            <Download className="w-4 h-4" />
            <span>Export Current View (CSV)</span>
          </button>
        </div>
      </div>

      {/* ── Stat Cards (sab loaded rows se derive hote hain) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Total Expected</span>
            <div className="p-2 bg-[#2e856e]/10 text-[#2e856e] rounded-xl">
              <IndianRupee className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">{formatINROrDash(stats.expected)}</div>
          <p className="text-xs text-gray-500 mt-1">Commission billable on disbursed files</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Received</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-700">{formatINROrDash(stats.received)}</div>
          <p className="text-xs text-gray-500 mt-1">Payout credited by lenders</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Pending</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-700">{formatINROrDash(stats.pending)}</div>
          <p className="text-xs text-gray-500 mt-1">Expected minus received</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-purple-600">Disbursed Files</span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {stats.disbursedFiles === null ? "—" : stats.disbursedFiles}
          </div>
          <p className="text-xs text-gray-500 mt-1">Files disbursed in selected period</p>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Lender */}
          <div>
            <Select
              value={lenderFilter}
              onChange={(val) => setLenderFilter(val)}
              className="w-full"
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder="Select lender"
              options={[{ value: "ALL", label: "All Lenders" }, ...lenders]}
            />
            {lendersError && (
              <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span>Lenders not loaded</span>
                <button onClick={fetchLenders} className="underline font-semibold">
                  Retry
                </button>
              </p>
            )}
          </div>

          {/* Status */}
          <div>
            <Select
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              className="w-full"
              size="large"
              options={[
                { value: "ALL", label: "All Statuses" },
                { value: "PENDING", label: "Pending" },
                { value: "RECEIVED", label: "Received" },
                { value: "PARTIAL", label: "Partial" },
              ]}
            />
          </div>

          {/* Date Range */}
          <div>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              format="DD/MM/YYYY"
              placeholder={["Start Date", "End Date"]}
              className="w-full rounded-xl border-gray-200 py-1.5 shadow-none"
            />
          </div>

          <div className="flex items-center">
            <button
              onClick={() => {
                setLenderFilter("ALL");
                setStatusFilter("ALL");
                setDateRange(null);
              }}
              className="w-full px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Commission Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
            />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center text-center gap-3 py-16 px-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <h2 className="text-base font-bold text-gray-900">Could not load DSA commissions</h2>
            <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
            <button
              onClick={fetchCommissions}
              className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                  <th className="py-4 px-4">Application ID</th>
                  <th className="py-4 px-4">Applicant</th>
                  <th className="py-4 px-4">Lender</th>
                  <th className="py-4 px-4 text-right">Disbursed Amount</th>
                  <th className="py-4 px-4 text-right">Commission %</th>
                  <th className="py-4 px-4 text-right">Expected Commission</th>
                  <th className="py-4 px-4 text-right">Received Amount</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4">Disbursal Date</th>
                  <th className="py-4 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-gray-400">
                      <Wallet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-semibold">No commission records found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Try changing filters, or refresh to fetch again
                      </p>
                      <button
                        onClick={fetchCommissions}
                        className="mt-3 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1.5"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, idx) => {
                    const statusInfo = STATUS_CONFIG[r.status] || {
                      label: r.status || "—",
                      bg: "bg-gray-100",
                      text: "text-gray-700",
                      border: "border-gray-200",
                    };
                    return (
                      <tr key={r.id ?? `${r.applicationId}-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3.5 px-4 font-bold text-gray-900">
                          <span className="font-mono text-xs px-2 py-1 bg-gray-100 rounded-lg">
                            {r.applicationId || "—"}
                          </span>
                        </td>

                        <td className="py-3.5 px-4 font-semibold text-gray-900">{r.applicantName || "—"}</td>

                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            <span>{r.lenderName || "—"}</span>
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-right font-bold text-gray-900">
                          {r.disbursedAmount === null ? "—" : formatINR(r.disbursedAmount)}
                        </td>

                        <td className="py-3.5 px-4 text-right font-semibold text-gray-700">
                          {r.commissionPercent === null ? "—" : `${r.commissionPercent}%`}
                        </td>

                        <td className="py-3.5 px-4 text-right font-black text-gray-900">
                          {r.expectedCommission === null ? "—" : formatINR(r.expectedCommission)}
                        </td>

                        <td className="py-3.5 px-4 text-right font-bold text-emerald-700">
                          {r.receivedAmount === null ? "—" : formatINR(r.receivedAmount)}
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                          >
                            {statusInfo.label}
                          </span>
                        </td>

                        <td className="py-3.5 px-4 text-xs text-gray-500 whitespace-nowrap">
                          {fmtDate(r.disbursalDate)}
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          {r.status !== "RECEIVED" && canMarkReceived && r.id ? (
                            <button
                              onClick={() => openReceiveModal(r)}
                              className="px-2.5 py-1.5 text-xs font-bold rounded-xl border border-[#2e856e]/30 bg-[#2e856e]/5 text-[#2e856e] hover:bg-[#2e856e]/10 transition-all inline-flex items-center gap-1.5"
                              title="Record commission payout received from lender"
                            >
                              <BadgeCheck className="w-3.5 h-3.5" />
                              Mark Received
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Mark Commission Received Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-[#2e856e] font-bold text-base">
            <BadgeCheck className="w-5 h-5" />
            <span>Record Commission Payout</span>
          </div>
        }
        open={receiveModal.open}
        onOk={handleConfirmReceived}
        onCancel={closeReceiveModal}
        okText="Save Payout"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#2e856e", borderColor: "#2e856e" } }}
      >
        {receiveModal.row && (
          <div className="space-y-4 py-3 text-sm">
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
              <div className="font-bold text-gray-800">{receiveModal.row.applicantName || "Applicant"}</div>
              <div className="text-gray-500">
                App ID: {receiveModal.row.applicationId || "—"} | Lender: {receiveModal.row.lenderName || "—"}
              </div>
              <div className="text-gray-500 mt-1">
                Expected:{" "}
                <strong>
                  {receiveModal.row.expectedCommission === null
                    ? "—"
                    : formatINR(receiveModal.row.expectedCommission)}
                </strong>{" "}
                | Already received:{" "}
                <strong>
                  {receiveModal.row.receivedAmount === null ? "—" : formatINR(receiveModal.row.receivedAmount)}
                </strong>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Received Amount (₹) *</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                value={receiveModal.receivedAmount}
                onChange={(val) => setReceiveModal((prev) => ({ ...prev, receivedAmount: val }))}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Payout / UTR Reference</label>
                <Input
                  className="rounded-xl"
                  placeholder="e.g. UTR123456789"
                  value={receiveModal.reference}
                  onChange={(e) => setReceiveModal((prev) => ({ ...prev, reference: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Received Date *</label>
                <DatePicker
                  className="w-full rounded-xl"
                  format="DD/MM/YYYY"
                  value={receiveModal.receivedDate}
                  onChange={(val) => setReceiveModal((prev) => ({ ...prev, receivedDate: val }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
              <Input.TextArea
                rows={3}
                className="rounded-xl"
                placeholder="e.g. Part payout received against Q2 invoice, balance pending from lender"
                value={receiveModal.remarks}
                onChange={(e) => setReceiveModal((prev) => ({ ...prev, remarks: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
